import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import '../assets/styles/Webadmincontact.css';
import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";
import ChatSidebar from "./ChatSidebar";

// Import the real userService from services
import userService from '../../services/userService';
import socketService from '../../services/socketService';
import adminStaffService from '../../services/adminStaffService';

const Webadmincontact = () => {
  // EXACT React Native app state structure
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userNames, setUserNames] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [filterType, setFilterType] = useState(null);

  // Chat interface state
  const [selectedContact, setSelectedContact] = useState(null);

  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [inputValue, setInputValue] = useState('');
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [autoRefreshing] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [endChatConfirm, setEndChatConfirm] = useState(null); // conversationId to end
  const [showCallModal, setShowCallModal] = useState(false);

  // Current user identity — matches AdminChatListScreen.js
  const [myStaffId, setMyStaffId] = useState(null);
  const [isStaff, setIsStaff] = useState(false);

  useEffect(() => {
    try {
      // App reads user.permissions.admin_user_type to detect staff (AdminChatListScreen.js line 49)
      const userData = localStorage.getItem('user') ||
                       localStorage.getItem('rebax_user_data') ||
                       localStorage.getItem('authUser') ||
                       localStorage.getItem('admin_user');
      if (userData) {
        const user = JSON.parse(userData);
        setMyStaffId(user.id || user.user_id || null);
        const staffType = user.permissions?.admin_user_type || null;
        setIsStaff(staffType === 'customer_support');
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Socket listeners are registered once per filter change, so they read the
  // latest values through refs instead of the stale closure.
  const selectedContactRef = useRef(null);
  const myStaffIdRef = useRef(null);
  const isStaffRef = useRef(false);
  const loadConversationsRef = useRef(null);
  selectedContactRef.current = selectedContact;
  myStaffIdRef.current = myStaffId;
  isStaffRef.current = isStaff;

  // Initialize WebSocket connection - matches React Native pattern
  useEffect(() => {
    // Reuses the existing socket if already connected
    socketService.connect();
    setSocketConnected(socketService.getConnectionStatus());

    const reload = () => loadConversationsRef.current && loadConversationsRef.current();

    // Reload conversations on (re)connect to pick up messages missed while offline
    const onSocketConnected = () => {
      setSocketConnected(true);
      reload();
    };
    socketService.onConnected(onSocketConnected);

    // Chat claimed — staff only see their own claimed chats, so drop chats claimed by someone else
    const unsubClaimed = socketService.addChatClaimedListener((data) => {
      setConversations(prev => {
        if (isStaffRef.current && String(data.claimed_by) !== String(myStaffIdRef.current)) {
          return prev.filter(c => c.id != data.conversation_id);
        }
        return prev.map(c => c.id == data.conversation_id ? { ...c, admin_id: data.claimed_by } : c);
      });
    });

    // New unclaimed / assigned chat — reload full list
    const unsubUnclaimed = socketService.addChatUnclaimedListener(() => reload());

    const unsubEnded = socketService.addChatEndedListener((data) => {
      setConversations(prev => prev.map(c => c.id == data.conversation_id ? { ...c, status: 'closed' } : c));
      setSelectedContact(prev => prev && prev.id == data.conversation_id ? { ...prev, status: 'closed' } : prev);
    });

    // The user read our messages — turn admin messages' ticks blue
    const unsubRead = socketService.addChatMessagesReadListener((data) => {
      const current = selectedContactRef.current;
      if (!current || data.conversation_id != current.id) return;
      if (String(data.reader_id) === String(myStaffIdRef.current)) return;
      setMessages(prev => prev.map(m => m.sender_role === 'admin' ? { ...m, is_read: true } : m));
    });

    const unsubMessage = socketService.addChatMessageListener((msg) => {
      if (!msg || !msg.conversation_id) return;
      const current = selectedContactRef.current;
      const isOpen = current && msg.conversation_id == current.id;
      const fromAdmin = ['admin', 'super_admin', 'support', 'customer_support'].includes(msg.sender_role);

      setConversations(prev => {
        const exists = prev.find(c => c.id == msg.conversation_id);
        if (!exists) {
          // New conversation — reload full list
          reload();
          return prev;
        }
        const updated = prev.map(c => c.id == msg.conversation_id ? {
          ...c,
          last_message: msg.message || (msg.media_url ? 'Attachment' : 'Message'),
          last_message_at: msg.created_at,
          unread_count: (isOpen || fromAdmin) ? c.unread_count : (c.unread_count || 0) + 1
        } : c);
        return updated.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      });

      if (isOpen) {
        setMessages(prev => prev.find(m => m.id == msg.id) ? prev : [...prev, msg]);
        // Keep the open chat marked as read on the server
        if (!fromAdmin) userService.markAsRead(current.id, myStaffIdRef.current).catch(() => {});
      }
    });

    // Legacy event listeners for backward compatibility
    const handleConversationUpdate = (data) => {
      setConversations(prev => {
        const updated = prev.map(conv =>
          conv.id == data.conversation_id
            ? { ...conv, unread_count: data.unread_count, last_message: data.last_message, last_message_at: data.last_message_at }
            : conv
        );
        return updated.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      });
    };

    const handleMessageRead = (data) => {
      const current = selectedContactRef.current;
      if (current && data.conversation_id == current.id) {
        setMessages(prev => prev.map(m => m.id == data.message_id ? { ...m, is_read: true } : m));
      }
    };

    socketService.addEventListener('conversation_updated', handleConversationUpdate);
    socketService.addEventListener('message_read', handleMessageRead);

    loadConversations();

    return () => {
      unsubMessage();
      unsubClaimed();
      unsubUnclaimed();
      unsubEnded();
      unsubRead();
      socketService.removeEventListener('conversation_updated', handleConversationUpdate);
      socketService.removeEventListener('message_read', handleMessageRead);
      socketService.removeOnConnected(onSocketConnected);
    };
  }, [filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leave the open conversation room when leaving the page
  useEffect(() => () => {
    if (selectedContactRef.current) socketService.leaveConversation(selectedContactRef.current.id);
  }, []);

  // Add focus-based reload similar to React Native useFocusEffect
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !loading && !refreshing) {
        // Small delay to allow previous markAsRead to propagate
        setTimeout(() => {
          loadConversations();
          // Also catch up the open chat in case socket events were missed while hidden
          if (selectedContactRef.current) loadMessages(selectedContactRef.current);
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loading, refreshing]);

  // Auto-refresh conversations disabled
  // useEffect(() => {
  //   const conversationInterval = setInterval(() => {
  //     if (!loading && !refreshing) {
  //       loadConversations();
  //     }
  //   }, 30000); // 30 seconds

  //   return () => clearInterval(conversationInterval);
  // }, [loading, refreshing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh messages disabled
  // useEffect(() => {
  //   let messageInterval;

  //   if (selectedContact && !loadingMessages) {
  //     messageInterval = setInterval(() => {
  //       loadMessages(selectedContact);
  //     }, 10000); // 10 seconds
  //   }

  //   return () => {
  //     if (messageInterval) clearInterval(messageInterval);
  //   };
  // }, [selectedContact, loadingMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConversations = async () => {
    try {
      // Don't show loading spinner on background refreshes if we have data
      if (conversations.length === 0) setLoading(true);

      console.log('📞 Loading conversations with filterType:', filterType);

      const filters = {};
      if (filterType) filters.type = filterType;

      const response = await userService.getConversations(filters, { page: 1, limit: 50 });
      console.log('📋 Conversations API response:', response);

      if (response.success && response.data) {
        console.log('✅ Setting conversations:', response.data.length, 'conversations');
        setConversations(response.data);

        // Since API already provides user_details, we don't need to fetch separately
        // Just create userNames mapping for backward compatibility with ChatSidebar
        const newNames = {};
        response.data.forEach(conversation => {
          if (conversation.user_details) {
            newNames[conversation.user_id] = {
              name: conversation.user_details.name,
              first_name: conversation.user_details.name?.split(' ')[0] || '',
              last_name: conversation.user_details.name?.split(' ').slice(1).join(' ') || '',
              online_status: conversation.user_details.online_status || 'offline',
              last_seen: conversation.user_details.last_seen
            };
          }
        });

        console.log('👥 Setting userNames:', Object.keys(newNames).length, 'users');
        setUserNames(newNames);


      } else {
        console.error('❌ Failed to load conversations:', response.message);
        setConversations([]);
      }
    } catch (error) {
      console.error('💥 Error loading conversations:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  loadConversationsRef.current = loadConversations;

  const handleRefresh = () => {
    setRefreshing(true);
    loadConversations();
  };

  // Claim chat — matches AdminChatListScreen.js handleClaimChat exactly
  const handleClaimChat = async (conversation) => {
    try {
      const response = await adminStaffService.claimChat(conversation.id);
      if (response.success || response.data?.success) {
        loadConversations();
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Already Claimed: This chat has been claimed by another agent.');
        loadConversations();
      } else {
        toast.error('Error: Failed to claim chat. Please try again.');
      }
    }
  };

  const handleSelectContact = (conversation) => {
    // Staff must claim before opening — matches app navigateToChat guard
    if (isStaff && !conversation.admin_id && conversation.status === 'open') {
      return; // Can't open unclaimed chat as staff; use Claim button
    }

    console.log('👆 handleSelectContact called for:', conversation.id, 'with unread count:', conversation.unread_count);

    setSelectedContact(conversation);

    // Join the conversation room so its messages arrive in real time
    socketService.joinConversation(conversation.id);

    // Load messages for this conversation
    loadMessages(conversation);

    // Mark messages as read and update unread count only after API success
    markMessagesAsRead(conversation.id);
  };

  const markMessagesAsRead = async (conversationId) => {
    console.log('🔍 markMessagesAsRead called for conversation:', conversationId, 'type:', typeof conversationId);

    // Get current admin user ID - try multiple localStorage keys
    let currentUserId;
    try {
      // Try different possible localStorage keys
      let userData = localStorage.getItem('rebax_user_data') ||
                    localStorage.getItem('user') ||
                    localStorage.getItem('authUser') ||
                    localStorage.getItem('admin_user');

      if (userData) {
        const user = JSON.parse(userData);
        currentUserId = user.id || user.user_id;
        console.log('👤 Current admin user ID:', currentUserId, 'from userData:', user);
      } else {
        console.log('🔍 Available localStorage keys:', Object.keys(localStorage));
        console.warn('⚠️ No user data found - using fallback admin ID');
        // Fallback to a default admin ID for testing
        currentUserId = 'admin';
      }
    } catch (error) {
      console.error('❌ Error getting current user:', error);
      console.log('🔍 Available localStorage keys:', Object.keys(localStorage));
      // Fallback to a default admin ID for testing
      currentUserId = 'admin';
    }

    // IMMEDIATE optimistic update for better UX (like React Native app)
    console.log('⚡ Immediate optimistic update - resetting unread count to 0');
    setConversations(prev => {
      const updated = prev.map(c => {
        // Use loose equality to handle string vs number ID comparison
        if (c.id == conversationId) {
          console.log('📝 Found conversation for optimistic update:', c.id, 'Current unread:', c.unread_count);
          return { ...c, unread_count: 0 };
        }
        return c;
      });
      const updatedConv = updated.find(c => c.id == conversationId);
      console.log('📋 Optimistically updated conversation:', updatedConv);
      return updated;
    });

    try {
      // Call the API to mark messages as read (using same method as React Native app)
      console.log('📞 Calling userService.markAsRead with userId...');
      const response = await userService.markAsRead(conversationId, currentUserId);

      console.log('📨 API Response:', response);

      if (response.success) {
        console.log('✅ Messages marked as read successfully for conversation:', conversationId);
        // Optimistic update already applied, no need to update again
      } else {
        console.error('❌ Failed to mark messages as read:', response.message);
        console.error('❌ Full response:', response);

        // Revert optimistic update on API failure
        console.log('🔄 Reverting optimistic update due to API failure');
        loadConversations(); // Reload to get correct unread counts
      }
    } catch (error) {
      console.error('💥 Error marking messages as read:', error);
      console.error('💥 Error details:', error.message, error.stack);

      // Revert optimistic update on error
      console.log('🔄 Reverting optimistic update due to error');
      loadConversations(); // Reload to get correct unread counts
    }
  };

  const loadMessages = async (conversation) => {
    try {
      setLoadingMessages(true);
      console.log('Loading messages for conversation:', conversation.id);

      const payload = {
        conversation_id: conversation.id,
        pagination: { page: 1, limit: 50 }
      };

      const response = await userService.getSupportMessages(payload);

      // Ignore a slow response for a chat the admin already switched away from
      if (selectedContactRef.current && selectedContactRef.current.id != conversation.id) return;

      if (response.success && response.data) {
        const msgs = Array.isArray(response.data) ? response.data : [];
        // Keep socket messages that arrived while this request was in flight
        setMessages(prev => {
          const fetchedIds = new Set(msgs.map(m => String(m.id)));
          const live = prev.filter(m => m.conversation_id == conversation.id && !fetchedIds.has(String(m.id)));
          return [...msgs, ...live].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        });
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || !selectedContact || sending) return;

    try {
      setSending(true);

      const payload = {
        message: inputValue.trim(),
        conversation_id: selectedContact.id,
        sender_role: 'admin',
        user_id: selectedContact.user_id,
        user_type: selectedContact.user_type
      };

      const response = await userService.contactSupport(payload);

      if (response.success) {
        const sentMsg = response.data;
        setMessages(prev => prev.find(m => m.id === sentMsg.id) ? prev : [...prev, sentMsg]);
        setInputValue('');
      } else {
        toast.error('Failed to send message: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleEndChat = (conversationId) => {
    setEndChatConfirm(conversationId);
  };

  const confirmEndChat = async () => {
    const conversationId = endChatConfirm;
    setEndChatConfirm(null);
    try {
      await adminStaffService.endChat(conversationId);
      // Update selectedContact status immediately (like mobile going back to list)
      setSelectedContact(prev => prev ? { ...prev, status: 'closed' } : prev);
      loadConversations();
    } catch (e) {
      console.error('Error ending chat:', e);
      toast.error('Failed to end chat');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleAttachmentMenu = () => {
    setShowAttachmentMenu(!showAttachmentMenu);
  };

  const handleAttachmentOption = (type) => {
    console.log(`Selected attachment type: ${type}`);
    setShowAttachmentMenu(false);

    // Create file input based on type
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.style.display = 'none';

    // Set file type restrictions based on attachment type
    switch (type) {
      case 'image':
        fileInput.accept = 'image/*';
        break;
      case 'document':
        fileInput.accept = '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx';
        break;
      case 'audio':
        fileInput.accept = 'audio/*';
        break;
      case 'video':
        fileInput.accept = 'video/*';
        break;
      default:
        fileInput.accept = '*/*';
    }

    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        uploadFile(file, type);
      }
    };

    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  const uploadFile = async (file, type) => {
    if (!selectedContact || uploading) return;

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', selectedContact.id);
      formData.append('user_id', selectedContact.user_id);
      formData.append('type', type);

      const response = await userService.uploadChatFile(formData);

      if (response.success) {
        // Send a message with the file info
        const messagePayload = {
          conversation_id: selectedContact.id,
          sender_role: 'admin',
          user_id: selectedContact.user_id,
          user_type: selectedContact.user_type,
          message: '',
          media_url: response.data?.url || response.data?.file_url,
          media_type: type,
          media_name: file.name
        };

        const messageResponse = await userService.contactSupport(messagePayload);

        if (messageResponse.success) {
          const sentMsg = messageResponse.data;
          setMessages(prev => prev.find(m => m.id === sentMsg.id) ? prev : [...prev, sentMsg]);
        }
      } else {
        toast.error('Failed to upload file: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <div className="d-flex">
      <LeftNav />
      <div className="main-content flex-grow-1 ">
        <TopNav />
        <div className="contact-support-container">

          {/* Sidebar with contacts list */}
          <ChatSidebar
            contacts={conversations}
            userNames={userNames}
            selectedContact={selectedContact}
            onSelectContact={(contact) => {
              console.log('🎯 Webadmincontact: onSelectContact called with:', contact);
              handleSelectContact(contact);
            }}
            loading={loading}
            refreshing={refreshing}
            onRefresh={handleRefresh}
            activeTab={activeTab}
            onTabChange={(tab) => {
              console.log('📑 Tab changed to:', tab);
              setActiveTab(tab.id);
              setFilterType(tab.type);
            }}
            onEndChat={handleEndChat}
            myStaffId={myStaffId}
            isStaff={isStaff}
            onClaimChat={handleClaimChat}
          />

          {/* Chat area */}
          <div className="chat-area">
            <div className="contact-header">
              <div className="header-left">
                <div className="support-avatar">
                  {selectedContact ?
                    (selectedContact.user_details?.name?.charAt(0) ||
                     userNames[selectedContact.user_id]?.first_name?.charAt(0) ||
                     selectedContact.user_type?.charAt(0).toUpperCase() || 'U') :
                    'S'
                  }
                </div>
                <div className="header-info">
                  <h1 className="contact-title">
                    {selectedContact ?
                      (selectedContact.user_details?.name ||
                       userNames[selectedContact.user_id]?.name ||
                       userNames[selectedContact.user_id]?.first_name ||
                       `${selectedContact.user_type || 'User'}`) :
                      'Select a conversation'
                    }
                  </h1>
                  <span className="contact-status">
                    {selectedContact ?
                      (selectedContact.user_details?.online_status === 'available' ||
                       selectedContact.user_details?.online_status === 'busy' ||
                       userNames[selectedContact.user_id]?.online_status === 'available' ||
                       userNames[selectedContact.user_id]?.online_status === 'busy' ? 'Online' : 'Offline') :
                      ''
                    }
                  </span>
                </div>
              </div>
              <div className="header-actions">
                {selectedContact && (
                  <button
                    onClick={() => handleEndChat(selectedContact.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      backgroundColor: '#FEE2E2', color: '#FF3B30',
                      border: 'none', borderRadius: '8px',
                      padding: '6px 14px', fontSize: '13px',
                      fontWeight: '600', cursor: 'pointer'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="#FF3B30" strokeWidth="2"/>
                      <path d="M15 9l-6 6M9 9l6 6" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    End Chat
                  </button>
                )}
                <button className="icon-btn" onClick={() => setShowCallModal(true)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="messages-container">
              {!selectedContact ? (
                <div className="no-conversation-selected">
                  <p>Select a conversation to start chatting</p>
                </div>
              ) : (
                <>
                  {loadingMessages ? (
                    <div className="loading-messages">
                      <p>Loading messages...</p>
                    </div>
                  ) : (
                    messages.map((message, index) => {
                      const isMyMessage = message.sender_role === 'admin';
                      const formatTime = (dateString) => {
                        if (!dateString) return '';
                        const date = new Date(dateString);
                        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      };

                      // System message — centered, no icon
                      if (message.message === 'Chat ended.' || message.message_type === 'system') {
                        return (
                          <div key={message.id || index} style={{
                            display: 'flex', justifyContent: 'center',
                            margin: '12px 0'
                          }}>
                            <span style={{
                              backgroundColor: '#F3F4F6', color: '#6B7280',
                              fontSize: '12px', padding: '4px 14px',
                              borderRadius: '12px', fontStyle: 'italic'
                            }}>
                              Chat ended.
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div key={message.id || index} className={`message ${isMyMessage ? 'user' : 'support'}`}>
                          {!isMyMessage && (
                            <div className="message-avatar">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                              </svg>
                            </div>
                          )}
                          <div className="message-bubble">
                            {message.deleted_for === 'everyone' ? (
                              <div className="deleted-message">
                                <em>This message was deleted</em>
                              </div>
                            ) : (
                              <>
                                {message.message && <div className="message-text">{message.message}</div>}
                                {message.media_url && (
                                  <div className="message-media">
                                    {message.media_type === 'image' ? (
                                      (() => {
                                        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
                                        let imageUrl;
                                        if (message.media_url.startsWith('http')) {
                                          // Already a full URL
                                          imageUrl = message.media_url;
                                        } else if (message.media_url.startsWith('/api/')) {
                                          // Already includes /api prefix - just add base domain
                                          imageUrl = `${apiBaseUrl}${message.media_url}`;
                                        } else {
                                          // Old format without /api prefix - add both base domain and /api
                                          imageUrl = `${apiBaseUrl}/api${message.media_url}`;
                                        }
                                        console.log('Image URL:', imageUrl, 'ENV API URL:', apiBaseUrl, 'Original media_url:', message.media_url);
                                        return (
                                          <img
                                            src={imageUrl}
                                            alt="attachment"
                                            className="message-image"
                                            onClick={() => window.open(imageUrl, '_blank')}
                                            onError={(e) => {
                                              console.error('Image failed to load:', e.target.src);
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        );
                                      })()
                                    ) : message.media_type === 'document' ? (
                                      (() => {
                                        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
                                        let documentUrl;
                                        if (message.media_url.startsWith('http')) {
                                          documentUrl = message.media_url;
                                        } else if (message.media_url.startsWith('/api/')) {
                                          documentUrl = `${apiBaseUrl}${message.media_url}`;
                                        } else {
                                          documentUrl = `${apiBaseUrl}/api${message.media_url}`;
                                        }
                                        return (
                                          <div className="message-document" onClick={() => window.open(documentUrl, '_blank')}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px'}}>
                                              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              <polyline points="13 2 13 9 20 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span>{message.media_name || 'Document'}</span>
                                          </div>
                                        );
                                      })()
                                    ) : (
                                      <div className="message-document">
                                        <span>📄 {message.media_name || 'File'}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                            <div className="message-meta">
                              <span className="message-time">{formatTime(message.created_at)}</span>
                              {message.is_edited && <span className="edited-indicator">(edited)</span>}
                              {isMyMessage && (
                                <div className="read-receipt">
                                  {message.is_read ? (
                                    <svg width="16" height="12" viewBox="0 0 16 12" fill="none" className="blue-tick">
                                      <path d="M1 6L4.5 9.5L15 1" stroke="#4285f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M1 6L4.5 9.5L10 1" stroke="#4285f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  ) : (
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="gray-tick">
                                      <path d="M1 4L4 7L11 1" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>

            {selectedContact && selectedContact.status === 'closed' && (
              <div style={{
                padding: '14px', textAlign: 'center',
                backgroundColor: '#F3F4F6', borderTop: '1px solid #E5E7EB',
                color: '#6B7280', fontSize: '13px', fontWeight: '500'
              }}>
                This conversation has ended
              </div>
            )}

            {selectedContact && selectedContact.status !== 'closed' && (
              <div className="input-container">
              <div className="attachment-wrapper">
                <button className="attachment-btn" onClick={toggleAttachmentMenu} disabled={uploading}>
                  {uploading ? (
                    <div className="sending-spinner">⟳</div>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>

                {showAttachmentMenu && (
                  <div className="attachment-menu">
                    <div className="attachment-option" onClick={() => handleAttachmentOption('document')}>
                      <div className="attachment-icon document-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="13 2 13 9 20 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>Document</span>
                    </div>

                    <div className="attachment-option" onClick={() => handleAttachmentOption('image')}>
                      <div className="attachment-icon image-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="white" strokeWidth="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="white"/>
                          <polyline points="21 15 16 10 5 21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>Photos</span>
                    </div>

                    <div className="attachment-option" onClick={() => handleAttachmentOption('audio')}>
                      <div className="attachment-icon audio-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M9 18V5l12-2v13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="6" cy="18" r="3" stroke="white" strokeWidth="2"/>
                          <circle cx="18" cy="16" r="3" stroke="white" strokeWidth="2"/>
                        </svg>
                      </div>
                      <span>Audio</span>
                    </div>

                    <div className="attachment-option" onClick={() => handleAttachmentOption('video')}>
                      <div className="attachment-icon video-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <polygon points="23 7 16 12 23 17 23 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>Video</span>
                    </div>
                  </div>
                )}
              </div>

              <textarea
                className="message-input"
                placeholder="Type a message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={sending}
                rows={1}
                style={{ resize: 'none', overflow: 'hidden' }}
              />
              <button
                className="send-button"
                onClick={handleSend}
                disabled={sending || !inputValue.trim()}
              >
                {sending ? (
                  <div className="sending-spinner">⟳</div>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 8L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Call Feature Modal */}
    {showCallModal && (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px', padding: '32px 24px',
          width: '300px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📱</div>
          <h5 style={{ fontWeight: '700', marginBottom: '8px' }}>Feature Not Available</h5>
          <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '20px' }}>
            To use this Feature Use the App
          </p>
          <button
            onClick={() => setShowCallModal(false)}
            style={{
              width: '100%', padding: '10px', border: 'none',
              borderRadius: '8px', backgroundColor: '#4F46E5', cursor: 'pointer',
              fontWeight: '600', color: '#fff', fontSize: '14px'
            }}
          >
            OK
          </button>
        </div>
      </div>
    )}

    {/* End Chat Confirmation Modal */}
    {endChatConfirm && (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
          width: '300px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}>
          <h5 style={{ fontWeight: '700', marginBottom: '8px' }}>End Chat</h5>
          <p style={{ color: '#6B7280', marginBottom: '20px', fontSize: '14px' }}>
            Are you sure you want to end this conversation?
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => setEndChatConfirm(null)}
              style={{
                flex: 1, padding: '10px', border: '1px solid #D1D5DB',
                borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer',
                fontWeight: '600', color: '#374151'
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmEndChat}
              style={{
                flex: 1, padding: '10px', border: 'none',
                borderRadius: '8px', backgroundColor: '#FF3B30', cursor: 'pointer',
                fontWeight: '600', color: '#fff'
              }}
            >
              End Chat
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Webadmincontact;
